import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy, Crown, ShieldCheck, DollarSign, Users, Tag, Flame,
  ChevronRight, ArrowLeft, Medal, Star, TrendingUp, Coins, Heart, Copy, Check
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const MEDAL_COLORS = ['text-amber-400', 'text-zinc-300', 'text-amber-700'];
const MEDAL_BG = ['bg-amber-500/15 border-amber-500/30', 'bg-zinc-400/10 border-zinc-400/20', 'bg-amber-800/10 border-amber-800/20'];

export default function LeaderboardPage({ onBack }) {
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('creators'); // creators | codes | supporters
  const [copiedCode, setCopiedCode] = useState('');

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leaderboard`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  if (loading) {
    return (
      <div className="p-5 flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="p-5 text-zinc-500">Failed to load leaderboard</p>;

  const impact = data.community_impact;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button
          data-testid="leaderboard-back-btn"
          onClick={onBack}
          className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-base text-white">Community Leaderboard</h2>
          <p className="text-[10px] text-zinc-500">Protecting creators together</p>
        </div>
        <Trophy className="w-5 h-5 text-amber-500" />
      </div>

      {/* Community Impact Banner */}
      <div className="glass-card rounded-xl p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-purple-500/5" />
        <div className="relative">
          <p className="text-[10px] font-bold text-zinc-400 tracking-wider mb-3">COMMUNITY IMPACT</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="font-heading font-black text-lg text-emerald-400">${impact.total_revenue_protected.toLocaleString()}</p>
              <p className="text-[9px] text-zinc-500">Revenue Protected</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-black text-lg text-amber-400">{impact.total_links_protected.toLocaleString()}</p>
              <p className="text-[9px] text-zinc-500">Links Protected</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-black text-lg text-purple-400">${impact.total_code_savings.toLocaleString()}</p>
              <p className="text-[9px] text-zinc-500">Saved by Codes</p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-white/5">
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] text-zinc-400">{impact.total_active_users} users</span>
            </div>
            <div className="flex items-center gap-1">
              <Heart className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] text-zinc-400">{impact.total_unique_creators} creators</span>
            </div>
            <div className="flex items-center gap-1">
              <Tag className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] text-zinc-400">{impact.total_code_uses} code uses</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 bg-white/[0.03] rounded-lg p-0.5">
        {[
          { id: 'creators', label: 'Creators', icon: Crown },
          { id: 'codes', label: 'Best Codes', icon: Tag },
          { id: 'supporters', label: 'Top Fans', icon: Star },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`leaderboard-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[10px] font-medium transition-all ${
              tab === t.id
                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
            }`}
          >
            <t.icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Creator Rankings */}
      {tab === 'creators' && (
        <div className="space-y-2">
          {data.creator_ranks.length === 0 ? (
            <EmptyState icon={Crown} text="No creator data yet" sub="Protect some links to see rankings!" />
          ) : (
            data.creator_ranks.map((c, i) => (
              <motion.div
                key={c.creator}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                className={`glass-card rounded-xl px-3.5 py-3 flex items-center gap-3 ${
                  i < 3 ? 'border ' + MEDAL_BG[i] : ''
                }`}
                data-testid={`creator-rank-${i}`}
              >
                {/* Rank */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  i < 3 ? MEDAL_BG[i] : 'bg-white/5'
                }`}>
                  {i < 3 ? (
                    <Trophy className={`w-3.5 h-3.5 ${MEDAL_COLORS[i]}`} />
                  ) : (
                    <span className="text-[10px] font-mono font-bold text-zinc-500">{i + 1}</span>
                  )}
                </div>

                {/* Creator Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate">{c.creator}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] text-zinc-500">{c.supporter_count} supporter{c.supporter_count !== 1 ? 's' : ''}</span>
                    <span className="text-[9px] text-zinc-600">{c.networks.length} network{c.networks.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-mono font-bold text-emerald-400">${c.revenue_protected}</p>
                  <p className="text-[9px] text-zinc-500">{c.total_links} links</p>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Code Rankings */}
      {tab === 'codes' && (
        <div className="space-y-2">
          {data.code_ranks.length === 0 ? (
            <EmptyState icon={Tag} text="No codes tracked yet" sub="Creator codes will appear here" />
          ) : (
            data.code_ranks.map((c, i) => (
              <motion.div
                key={c.code}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                className={`glass-card rounded-xl px-3.5 py-3 ${
                  i < 3 ? 'border ' + MEDAL_BG[i] : ''
                }`}
                data-testid={`code-rank-${i}`}
              >
                <div className="flex items-center gap-3">
                  {/* Rank */}
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    i < 3 ? MEDAL_BG[i] : 'bg-white/5'
                  }`}>
                    {i < 3 ? (
                      <Medal className={`w-3.5 h-3.5 ${MEDAL_COLORS[i]}`} />
                    ) : (
                      <span className="text-[10px] font-mono font-bold text-zinc-500">{i + 1}</span>
                    )}
                  </div>

                  {/* Code Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-white bg-white/5 px-1.5 py-0.5 rounded">{c.code}</span>
                      {c.verified && <ShieldCheck className="w-3 h-3 text-emerald-400" />}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] text-zinc-400">{c.creator_name}</span>
                      <span className="text-[9px] text-zinc-600">at {c.store}</span>
                    </div>
                  </div>

                  {/* Copy */}
                  <button
                    data-testid={`copy-leaderboard-code-${i}`}
                    onClick={() => copyCode(c.code)}
                    className="p-1.5 rounded-md text-zinc-600 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
                  >
                    {copiedCode === c.code ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-3 mt-2 pl-10">
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3 text-emerald-400" />
                    <span className="text-[10px] font-mono text-emerald-400">${c.estimated_savings} saved</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag className="w-3 h-3 text-zinc-500" />
                    <span className="text-[10px] text-zinc-500">{c.uses} uses</span>
                  </div>
                  {c.discount_value > 0 && (
                    <span className="text-[10px] text-purple-400">
                      {c.discount_value}{c.discount_type === 'percentage' ? '%' : '$'} off
                    </span>
                  )}
                  <span className={`text-[10px] ${c.success_rate >= 90 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {c.success_rate}% success
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Top Supporters */}
      {tab === 'supporters' && (
        <div className="space-y-2">
          {data.top_supporters.length === 0 ? (
            <EmptyState icon={Star} text="No supporter data yet" sub="Start protecting links to get ranked!" />
          ) : (
            data.top_supporters.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                className={`glass-card rounded-xl px-3.5 py-3 flex items-center gap-3 ${
                  i < 3 ? 'border ' + MEDAL_BG[i] : ''
                }`}
                data-testid={`supporter-rank-${i}`}
              >
                {/* Rank */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  i < 3 ? MEDAL_BG[i] : 'bg-white/5'
                }`}>
                  {i < 3 ? (
                    <Star className={`w-3.5 h-3.5 ${MEDAL_COLORS[i]}`} />
                  ) : (
                    <span className="text-[10px] font-mono font-bold text-zinc-500">{i + 1}</span>
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate">{s.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] text-amber-500">Lvl {s.level}</span>
                    <span className="text-[9px] text-zinc-500">{s.creators_count} creator{s.creators_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-mono font-bold text-emerald-400">${s.revenue_protected}</p>
                  <p className="text-[9px] text-zinc-500">{s.links_protected} links</p>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text, sub }) {
  return (
    <div className="text-center py-10">
      <Icon className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
      <p className="text-sm text-zinc-500">{text}</p>
      <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>
    </div>
  );
}
